import React, { useEffect, useState } from 'react';
import StaticMap from '@common/components/CleaningMap/StaticMap';
import DashboardMap from '@common/components/Dashboard/DashboardMap';
import DashboardStatusInfo from '@common/components/Dashboard/DashboardStatusInfo';
import CustomSnackbar from '@common/components/Feedback/CustomSnackbar';
import HttpFetchStatus from '@common/enums/api/http-fetch-status';
import { navigateSphereWithFilters } from '@common/enums/query-params/filters';
import {
  LocationsIcon,
  MoveRightArrow,
  NotSubscribedIcon,
  OrganizationsIcon,
  ShowMoreIcon,
  SubscribedIcon,
  RefreshIcon,
} from '@common/utils/icons/icons';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import OrganizationRole from '../../models/enums/organization-roles';
import BaseOrganization from '../../models/organization/base-organization';
import { useAppDispatch, useAppSelector } from '../../store';
import { postSubscribeToRobotById } from '../../store/general/general-actions';
import { resetUserRestStatus } from '../../store/general/general-slice';
import { selectUser } from '../../store/general/general-slice-selectors';
import { selectRobotsData } from '../../store/robots/robots-slice-selectors';
import { replaceArgsInMessage } from '../../utils/strings';
import TooltipIconButton from '@common/components/Inputs/TooltipIconButton';
import OrganizationInformationDialog from './OrganizationInformationDialog';
import RobotCommandButtons from './RobotCommandButtons';
import RobotStatusLists from './status-info/RobotStatusLists';
import { useResizeDetector } from 'react-resize-detector';
import RobotConnectionIcon from './status-info/RobotConnectionIcon';
import { sendRobotWebsocketMessage } from 'store/middleware/robot-websocket-middleware';
import MessageType from '@common/enums/robot/rsw/message-types';
import CircularProgress from '@mui/material/CircularProgress';
import DialogWrapperWithClose from '@common/components/General/DialogWrapperWithClose';

import Tooltip from '@mui/material/Tooltip';
import { isVersionAGreaterThanB } from '@common/utils/version-util';
import { getCurrentPageName, trackEvent } from 'services/analytics';
import { AnalyticsEvent } from 'services/analytics-events';

const RobotInfo: React.FC<{
  onRoomNavigateClick: () => void;
}> = ({ onRoomNavigateClick }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const { height: statusInfoWrapperHeight, ref: statusInfoWrapperRef } =
    useResizeDetector<HTMLDivElement>();

  const [openOrganizationInformationDialog, setOpenOrganizationInformationDialog] =
    useState<BaseOrganization | null>(null);
  const [showAll, setShowAll] = useState(false);
  const [isFetchingLiveSnapshot, setIsFetchingLiveSnapshot] = useState(false);
  const [isMapFullScreen, setIsMapFullScreen] = useState(false);

  const robotsData = useAppSelector((state) => selectRobotsData(state));
  const user = useAppSelector((state) => selectUser(state));

  const robot = robotsData.currentRobot;

  useEffect(() => {
    setIsFetchingLiveSnapshot(false);
  }, [robotsData.currentRobotData.coverageMap]);

  if (robot == null) {
    return null;
  }

  const toggleSubscribeClickHandler = () => {
    dispatch(postSubscribeToRobotById(robot.id));
  };

  const closeSubscribeSnackbarHandler = () => {
    dispatch(
      resetUserRestStatus({
        postSubscribeRobotStatus: HttpFetchStatus.Idle,
        postSubscribeRobotError: null,
      }),
    );
  };

  const isActiveRobot = robot.deploymentDates.end == null && robot.status != null;
  const isSubscribed = user.data.robotSubscriptions.some((r) => r.id === robot.id);

  const sortOrganizations = (organizationParentChain: BaseOrganization[]) => {
    const sorted = [];
    let higherParent = organizationParentChain.find((org) => {
      return org.parentId === null || !organizationParentChain.find((o) => o.id === org.parentId);
    });

    while (higherParent) {
      sorted.unshift(higherParent);
      const parentId = higherParent.id;
      higherParent = organizationParentChain.find((o) => o.parentId === parentId);
    }

    return sorted;
  };

  const sortedOrganizations = robot.organizationParentChain
    ? sortOrganizations(robot.organizationParentChain)
    : [];
  const visibleOrganizations = showAll ? sortedOrganizations : sortedOrganizations.slice(0, 2);

  const openOrganizationInformationDialogHandler = (organizationId: string) => {
    const organizationInParentChain = robot.organizationParentChain?.find(
      (org) => org.id === organizationId,
    );
    if (organizationInParentChain) {
      setOpenOrganizationInformationDialog(organizationInParentChain);
    }
  };

  const closeOrganizationInformationDialogHandler = () => {
    setOpenOrganizationInformationDialog(null);
  };

  const navigateToOrganizationClickHandler = (organizationId: string) => {
    navigate(navigateSphereWithFilters(organizationId, true));
  };

  const organizationChainClickHandler = (canAccess: boolean | undefined, orgId: string) => {
    if (canAccess) {
      navigateToOrganizationClickHandler(orgId);
    } else {
      openOrganizationInformationDialogHandler(orgId);
    }
  };

  const expandOrganizationChainHandler = () => {
    setShowAll(true);
  };

  const fetchLiveSnapshotClickHandler = () => {
    trackEvent(AnalyticsEvent.LiveSnapshotRequested, {
      robot_id: robot.id,
      robot_serial: robot.serialNumber,
      source_page: getCurrentPageName() ?? 'unknown',
    });
    setIsFetchingLiveSnapshot(true);
    dispatch(
      sendRobotWebsocketMessage({
        type: MessageType.GetLiveStatus,
        message: null,
      }),
    );
  };

  const mapFullScreenClickHandler = () => {
    setIsMapFullScreen((prevState) => !prevState);
  };

  return (
    <React.Fragment>
      <Stack spacing={2} sx={{ px: { xs: 1, sm: 0 } }}>
        <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
          {!isActiveRobot && (
            <Chip label={t('info-deployment-ended')} variant="filled" color="warning" />
          )}
          <Chip label={t('robot-model-' + robot.model)} variant="outlined" />
          {isActiveRobot && (
            <React.Fragment>
              <TooltipIconButton
                tooltip={replaceArgsInMessage(
                  isSubscribed ? t('button-subscribed') : t('button-not-subscribed'),
                  [robot.name],
                )}
                onClick={toggleSubscribeClickHandler}
                icon={
                  isSubscribed ? (
                    <SubscribedIcon />
                  ) : (
                    <NotSubscribedIcon data-testid="button-subscribe" />
                  )
                }
                color="primary"
              />
              <RobotConnectionIcon />
            </React.Fragment>
          )}
        </Stack>

        <RobotCommandButtons />

        <Stack sx={{ flexDirection: { xs: 'column', xl: 'row' }, gap: 2, alignItems: 'stretch' }}>
          {robot.softwareVersion?.rswSoftwareVersion != null &&
            !isVersionAGreaterThanB('2025.2.0', robot.softwareVersion.rswSoftwareVersion) && (
              <Box
                sx={{
                  height: { xs: '30vh', xl: statusInfoWrapperHeight ?? '100%' },
                  width: { xs: '100%', xl: '50%' },
                }}
              >
                <DashboardMap
                  showEnableFullScreenAlways
                  mapLoadingErrorProps={{ errorText: t('dashboard-cannot-load-map') }}
                  fullScreenProps={{
                    isFullScreen: isMapFullScreen,
                    onFullScreenClick: mapFullScreenClickHandler,
                  }}
                  additionalControls={
                    <Tooltip title={t('info-tooltip-fetch-live-snapshot')}>
                      <span>
                        <IconButton
                          onClick={fetchLiveSnapshotClickHandler}
                          disabled={!robotsData.currentRobot?.status?.active}
                        >
                          {isFetchingLiveSnapshot ? (
                            <CircularProgress size={24} />
                          ) : (
                            <RefreshIcon />
                          )}
                        </IconButton>
                      </span>
                    </Tooltip>
                  }
                >
                  <StaticMap />
                </DashboardMap>
              </Box>
            )}

          <div
            ref={statusInfoWrapperRef}
            style={{ alignSelf: 'flex-start', flex: 1, width: '100%' }}
          >
            <DashboardStatusInfo
              onRoomClick={onRoomNavigateClick}
              hideScheduling={
                robot.softwareVersion?.rswSoftwareVersion == null ||
                isVersionAGreaterThanB('2025.2.0', robot.softwareVersion.rswSoftwareVersion)
              }
            />
          </div>
        </Stack>

        <RobotStatusLists status={robot.status} />

        <Box>
          <Stack direction="row" alignItems="center" flexWrap="wrap" py={2} gap={2}>
            {visibleOrganizations.map((org, i) => (
              <React.Fragment key={org.id}>
                {i > 0 && <MoveRightArrow color="action" />}
                <Box sx={{ whiteSpace: 'nowrap' }}>
                  <Typography variant="subtitle2" color="textSecondary">
                    {t(`organization-role-${org.role}`)}
                  </Typography>
                  <Button
                    onClick={() => organizationChainClickHandler(org.canAccess, org.id)}
                    startIcon={
                      org.role === OrganizationRole.LOCATION ? (
                        <LocationsIcon color="action" />
                      ) : (
                        <OrganizationsIcon color="action" />
                      )
                    }
                  >
                    {org.name}
                  </Button>
                </Box>
              </React.Fragment>
            ))}
            {!showAll && sortedOrganizations.length > 2 && (
              <IconButton onClick={expandOrganizationChainHandler}>
                <ShowMoreIcon color="action" />
              </IconButton>
            )}
          </Stack>
        </Box>
      </Stack>
      <DialogWrapperWithClose onClose={mapFullScreenClickHandler} open={isMapFullScreen} fullScreen>
        <DashboardMap
          showEnableFullScreenAlways
          mapLoadingErrorProps={{ errorText: t('dashboard-cannot-load-map') }}
          fullScreenProps={{
            isFullScreen: isMapFullScreen,
            onFullScreenClick: mapFullScreenClickHandler,
          }}
          additionalControls={
            <IconButton
              onClick={fetchLiveSnapshotClickHandler}
              disabled={!robotsData.currentRobot?.status?.active}
            >
              {isFetchingLiveSnapshot ? <CircularProgress size={24} /> : <RefreshIcon />}
            </IconButton>
          }
        >
          <StaticMap />
        </DashboardMap>
      </DialogWrapperWithClose>
      <CustomSnackbar
        open={user.rest.postSubscribeRobotStatus === HttpFetchStatus.Success}
        title={replaceArgsInMessage(
          isSubscribed ? t('success-subscribing') : t('success-unsubscribing'),
          [robot.name],
        )}
        onClose={closeSubscribeSnackbarHandler}
      />
      <CustomSnackbar
        open={user.rest.postSubscribeRobotStatus === HttpFetchStatus.Failed}
        title={replaceArgsInMessage(
          isSubscribed ? t('error-subscribing') : t('error-unsubscribing'),
          [robot.name],
        )}
        onClose={closeSubscribeSnackbarHandler}
        severity="error"
      />
      <OrganizationInformationDialog
        open={openOrganizationInformationDialog !== null}
        onClose={closeOrganizationInformationDialogHandler}
        organizationInfo={openOrganizationInformationDialog}
      />
    </React.Fragment>
  );
};

export default RobotInfo;
